// src/OpenCloseChecklists.js
// Daily Opening / Closing checklists for staff. Each submission is saved to
// Firebase (checklists/submissions) as a permanent record and emailed to the
// owner. Admins can review history and edit the checklist items themselves.
import React, { useState, useEffect, useMemo } from "react";
import { database, ref, onValue, push, set, auth } from "./firebase";
import {
  Sunrise, Moon, History, Pencil, Plus, Trash2, Check, X,
  ClipboardCheck, Save, Search,
} from "lucide-react";

const OWNER_EMAIL = "masterchan@ixlma.com";

const uid = () => Math.random().toString(36).slice(2, 9);
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const prettyDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${m}/${d}/${y}`;
};

// Built-in defaults — used until an admin edits & saves their own version.
const DEFAULT_TEMPLATES = {
  opening: {
    title: "Opening Checklist",
    description: "This is the opening checklist for iXL Martial Arts",
    items: [
      "Turn on Lights",
      "Clock In: All hourly staff members are required to clock in using When I Work.",
      "Put down floor mats",
      "Make bathrooms customer friendly – trash, step stool, clean, toilet, paper towels, soap, odor, etc. (this should be checked throughout the day.)",
      "Log into iPads",
      "Unlock Doors",
      "Turn on Open Sign",
      "Change Open Sign to: Welcome any student coming in today, any new students, Happy B-Days or Current Trial Offer",
      "Turn on music",
      "Turn on all TVs and have slideshow running",
      "Place boards, stars, pads and any special items ready for instructor to start first class &/or trial",
      "Update and print off announcement list (Mon)",
      "Straighten pads, sweep mats, cleanup/organize workout area",
      "Review already prepared action plan from yesterday; look on SPARKMember to see if calendar has changed since Action Plan was created",
      "Pull out files, papers, & materials needed for Today's appointments",
      "Wed: ABC List (go ahead and check this off to send the list, but make sure it's actually performed.)",
      "Clean windows & mirror on Tue/Thur",
      "Text Master Chan if you're successful with a one-b4-one (Monday thru Friday Only)",
      "Check emails and respond to any not responded to yet. Make sure to carbon copy Master Chan to the email reply.",
    ].map(text => ({ id: uid(), text })),
    hasAccounting: false,
    hasRecap: false,
    notesLabel: "Add any notes, questions or concerns here:",
    notesRequired: true,
  },
  closing: {
    title: "Closing Checklist",
    description: "This is the closing checklist for iXL Martial Arts",
    items: [
      "Take out garbage – back room, bathrooms, office, front, & shredder",
      "Put floor mats on the rail",
      "Straighten pads & put equipment where it belongs",
      "Turn off TV's",
      "Clean bathrooms – stool and trash can on bench, wipe down sink & toilets, pick up trash, sweep & mop",
      "Sweep concrete area, storage room & back room",
      "Sweep & mop mats, this can be done in the morning if need be.",
      "Turn off Open Sign",
      "Put all papers & unfinished work in top tray – clean up office area",
      "Turn off music",
      "Mop concrete area (incl Bathrooms)",
      "Turn off lights",
      "Lock Doors",
    ].map(text => ({ id: uid(), text })),
    hasAccounting: true,
    hasRecap: true,
    notesLabel: "Add any notes, questions or concerns here:",
    notesRequired: false,
  },
};

// Ensure a template loaded from the DB has all fields + item ids.
function normalizeTemplate(t, fallback) {
  if (!t) return fallback;
  return {
    title: t.title || fallback.title,
    description: t.description || fallback.description,
    items: (t.items || fallback.items).map(it =>
      typeof it === "string" ? { id: uid(), text: it } : { id: it.id || uid(), text: it.text || "" }
    ),
    hasAccounting: t.hasAccounting != null ? t.hasAccounting : fallback.hasAccounting,
    hasRecap: t.hasRecap != null ? t.hasRecap : fallback.hasRecap,
    notesLabel: t.notesLabel || fallback.notesLabel,
    notesRequired: t.notesRequired != null ? t.notesRequired : fallback.notesRequired,
  };
}

export default function OpenCloseChecklists({ isAdmin = false }) {
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [submissions, setSubmissions] = useState({});
  const [tab, setTab] = useState("opening"); // opening | closing | history | edit

  useEffect(() => {
    return onValue(ref(database, "checklists/templates"), snap => {
      const v = snap.val() || {};
      setTemplates({
        opening: normalizeTemplate(v.opening, DEFAULT_TEMPLATES.opening),
        closing: normalizeTemplate(v.closing, DEFAULT_TEMPLATES.closing),
      });
    });
  }, []);
  useEffect(() => {
    return onValue(ref(database, "checklists/submissions"), snap => setSubmissions(snap.val() || {}));
  }, []);

  const TABS = [
    { key: "opening", label: "Opening", icon: Sunrise },
    { key: "closing", label: "Closing", icon: Moon },
    ...(isAdmin ? [
      { key: "history", label: "History", icon: History },
      { key: "edit", label: "Edit Lists", icon: Pencil },
    ] : []),
  ];

  return (
    <div className="page-content">
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0, marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
          <ClipboardCheck size={24} color="#7c3aed" /> Open / Close Checklists
        </h2>
        <p style={{ marginTop: 0, color: "#64748b", fontSize: 14 }}>
          Complete the opening or closing checklist each day. A copy is saved and emailed to the owner.
        </p>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, margin: "18px 0 24px", borderBottom: "3px solid #e5e7eb", flexWrap: "wrap" }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "10px 18px",
                  background: active ? "#7c3aed" : "transparent", color: active ? "#fff" : "#374151",
                  border: "none", borderBottom: active ? "3px solid #7c3aed" : "3px solid transparent",
                  marginBottom: -3, cursor: "pointer", fontWeight: active ? 600 : 500, fontSize: 15,
                  borderRadius: active ? "8px 8px 0 0" : 0,
                }}>
                <Icon size={17} /> {t.label}
              </button>
            );
          })}
        </div>

        {(tab === "opening" || tab === "closing") && (
          <FillForm key={tab} type={tab} template={templates[tab]} />
        )}
        {tab === "history" && isAdmin && (
          <HistoryTab submissions={submissions} />
        )}
        {tab === "edit" && isAdmin && (
          <EditTab templates={templates} />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── FILL FORM ─────────────────────────── */
function FillForm({ type, template }) {
  const me = auth.currentUser;
  const myName = me?.displayName || (me?.email ? me.email.split("@")[0] : "");
  const [date, setDate] = useState(todayStr());
  const [staffOne, setStaffOne] = useState(myName);
  const [staffTwo, setStaffTwo] = useState("");
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState("");
  const [endOfDay, setEndOfDay] = useState("");
  const [monthToDate, setMonthToDate] = useState("");
  const [recap, setRecap] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const items = template.items || [];
  const checkedCount = items.filter(it => checked[it.id]).length;

  const toggle = (id) => setChecked(c => ({ ...c, [id]: !c[id] }));

  const submit = async () => {
    setError("");
    if (!staffOne.trim()) { setError("Please enter who is opening/closing (Staff One)."); return; }
    if (template.notesRequired && !notes.trim()) { setError("Please add notes (type 'none' if there are none)."); return; }
    if (template.hasAccounting && (!endOfDay.trim() || !monthToDate.trim())) { setError("Please fill in the accounting amounts."); return; }
    if (template.hasRecap && !recap.trim()) { setError("Please fill in today's recap and action plan."); return; }

    setSaving(true);
    const submission = {
      type,
      title: template.title,
      dateStr: date,
      staffOne: staffOne.trim(),
      staffTwo: staffTwo.trim() || null,
      submittedByUid: me?.uid || null,
      submittedByEmail: me?.email || null,
      submittedAt: new Date().toISOString(),
      items: items.map(it => ({ text: it.text, done: !!checked[it.id] })),
      checkedCount,
      totalCount: items.length,
      notes: notes.trim() || null,
      endOfDay: template.hasAccounting ? (endOfDay.trim() || null) : null,
      monthToDate: template.hasAccounting ? (monthToDate.trim() || null) : null,
      recap: template.hasRecap ? (recap.trim() || null) : null,
    };
    try {
      const refNew = push(ref(database, "checklists/submissions"));
      await set(refNew, submission);
      // Fire-and-forget email to the owner (non-blocking).
      try {
        await fetch("/.netlify/functions/send-checklist-submission", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...submission, ownerEmail: OWNER_EMAIL, id: refNew.key }),
        });
      } catch (e) { /* email is best-effort */ }
      setDone(true);
    } catch (e) {
      setError("Could not save the checklist. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div style={{ ...card, textAlign: "center", padding: 44 }}>
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Check size={30} color="#16a34a" />
        </div>
        <h3 style={{ margin: "0 0 6px" }}>{template.title} submitted</h3>
        <p style={{ color: "#64748b", margin: "0 0 20px" }}>Saved and emailed to the owner. Thank you!</p>
        <button onClick={() => { setDone(false); setChecked({}); setNotes(""); setEndOfDay(""); setMonthToDate(""); setRecap(""); setStaffTwo(""); setDate(todayStr()); }}
          style={btnPrimary}>Start another</button>
      </div>
    );
  }

  return (
    <div style={card}>
      <h3 style={{ marginTop: 0, marginBottom: 4 }}>{template.title}</h3>
      <p style={{ marginTop: 0, color: "#64748b", fontSize: 14 }}>{template.description}</p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, margin: "16px 0" }}>
        <div><label style={lbl}>Today's Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} /></div>
        <div><label style={lbl}>Staff One</label><input value={staffOne} onChange={e => setStaffOne(e.target.value)} style={inp} placeholder="Name" /></div>
        <div><label style={lbl}>Staff Two</label><input value={staffTwo} onChange={e => setStaffTwo(e.target.value)} style={inp} placeholder="Name (optional)" /></div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "8px 0" }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{template.title}</div>
        <div style={{ fontSize: 13, color: "#7c3aed", fontWeight: 600 }}>{checkedCount} / {items.length} done</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map(it => (
          <label key={it.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 8px", borderRadius: 8, cursor: "pointer", background: checked[it.id] ? "#f0fdf4" : "transparent" }}>
            <input type="checkbox" checked={!!checked[it.id]} onChange={() => toggle(it.id)} style={{ width: 20, height: 20, marginTop: 1, accentColor: "#16a34a", flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: "#1f2937", textDecoration: checked[it.id] ? "line-through" : "none", opacity: checked[it.id] ? 0.65 : 1 }}>{it.text}</span>
          </label>
        ))}
      </div>

      {template.hasAccounting && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 18 }}>
          <div><label style={lbl}>End of day accounting amount *</label><input value={endOfDay} onChange={e => setEndOfDay(e.target.value)} style={inp} placeholder="$0.00" /></div>
          <div><label style={lbl}>Month to date accounting *</label><input value={monthToDate} onChange={e => setMonthToDate(e.target.value)} style={inp} placeholder="$0.00" /></div>
        </div>
      )}
      {template.hasRecap && (
        <div style={{ marginTop: 14 }}>
          <label style={lbl}>Today's Recap and Action Plan for tomorrow. Be as detailed as possible. *</label>
          <textarea value={recap} onChange={e => setRecap(e.target.value)} rows={5} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <label style={lbl}>{template.notesLabel}{template.notesRequired ? " *" : ""}</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }}
          placeholder={template.notesRequired ? "If there are none, type 'none' to continue." : "Optional"} />
      </div>

      {error && <div style={{ color: "#dc2626", fontSize: 13, marginTop: 12, fontWeight: 600 }}>{error}</div>}

      <div style={{ marginTop: 18 }}>
        <button onClick={submit} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
          {saving ? "Submitting…" : "Submit checklist"}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── HISTORY (admin) ─────────────────────────── */
function HistoryTab({ submissions }) {
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);

  const rows = useMemo(() => {
    return Object.entries(submissions)
      .map(([id, s]) => ({ id, ...s }))
      .filter(s => typeFilter === "all" || s.type === typeFilter)
      .filter(s => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (s.staffOne || "").toLowerCase().includes(q)
          || (s.staffTwo || "").toLowerCase().includes(q)
          || (s.dateStr || "").includes(q)
          || (s.title || "").toLowerCase().includes(q);
      })
      .sort((a, b) => (b.submittedAt || "").localeCompare(a.submittedAt || ""));
  }, [submissions, typeFilter, search]);

  return (
    <div style={card}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ ...inp, width: "auto", marginBottom: 0 }}>
          <option value="all">All checklists</option>
          <option value="opening">Opening only</option>
          <option value="closing">Closing only</option>
        </select>
        <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
          <Search size={15} style={{ position: "absolute", left: 10, top: 11, color: "#9ca3af" }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search staff, date…" style={{ ...inp, marginBottom: 0, paddingLeft: 32 }} />
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ textAlign: "center", color: "#94a3b8", padding: 30 }}>No checklists submitted yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map(s => {
            const isOpen = openId === s.id;
            return (
              <div key={s.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                <button onClick={() => setOpenId(isOpen ? null : s.id)}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 14px", background: "#f8fafc", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, marginRight: 8, background: s.type === "opening" ? "#fef3c7" : "#ede9fe", color: s.type === "opening" ? "#92400e" : "#6d28d9" }}>
                      {s.type === "opening" ? "OPENING" : "CLOSING"}
                    </span>
                    <span style={{ fontWeight: 600 }}>{prettyDate(s.dateStr)}</span>
                    <span style={{ color: "#64748b", fontSize: 13 }}> · {s.staffOne}{s.staffTwo ? ` & ${s.staffTwo}` : ""}</span>
                  </div>
                  <span style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, flexShrink: 0 }}>{s.checkedCount}/{s.totalCount}</span>
                </button>
                {isOpen && (
                  <div style={{ padding: "14px 16px", fontSize: 14 }}>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>
                      Submitted {s.submittedAt ? new Date(s.submittedAt).toLocaleString() : ""}{s.submittedByEmail ? ` · ${s.submittedByEmail}` : ""}
                    </div>
                    {(s.items || []).map((it, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "3px 0" }}>
                        {it.done ? <Check size={15} color="#16a34a" style={{ marginTop: 2, flexShrink: 0 }} /> : <X size={15} color="#cbd5e1" style={{ marginTop: 2, flexShrink: 0 }} />}
                        <span style={{ color: it.done ? "#1f2937" : "#94a3b8" }}>{it.text}</span>
                      </div>
                    ))}
                    {(s.endOfDay || s.monthToDate) && (
                      <div style={{ marginTop: 12, display: "flex", gap: 24 }}>
                        {s.endOfDay && <div><div style={lbl}>End of day</div><div style={{ fontWeight: 700 }}>{s.endOfDay}</div></div>}
                        {s.monthToDate && <div><div style={lbl}>Month to date</div><div style={{ fontWeight: 700 }}>{s.monthToDate}</div></div>}
                      </div>
                    )}
                    {s.recap && <div style={{ marginTop: 12 }}><div style={lbl}>Recap / Action plan</div><div style={{ whiteSpace: "pre-wrap" }}>{s.recap}</div></div>}
                    {s.notes && <div style={{ marginTop: 12 }}><div style={lbl}>Notes</div><div style={{ whiteSpace: "pre-wrap" }}>{s.notes}</div></div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── EDIT (admin) ─────────────────────────── */
function EditTab({ templates }) {
  const [which, setWhich] = useState("opening");
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(templates)));
  const [savedMsg, setSavedMsg] = useState("");

  // Re-seed the draft if templates change and nothing edited yet — keep simple: only on mount/which change from DB
  useEffect(() => { setDraft(JSON.parse(JSON.stringify(templates))); /* eslint-disable-next-line */ }, []);

  const t = draft[which];
  const setField = (field, value) => setDraft(d => ({ ...d, [which]: { ...d[which], [field]: value } }));
  const setItemText = (id, text) => setField("items", t.items.map(it => it.id === id ? { ...it, text } : it));
  const addItem = () => setField("items", [...t.items, { id: uid(), text: "" }]);
  const removeItem = (id) => setField("items", t.items.filter(it => it.id !== id));
  const moveItem = (idx, dir) => {
    const arr = [...t.items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setField("items", arr);
  };

  const save = async () => {
    const clean = {
      title: t.title, description: t.description,
      items: t.items.filter(it => (it.text || "").trim()).map(it => ({ id: it.id, text: it.text.trim() })),
      hasAccounting: !!t.hasAccounting, hasRecap: !!t.hasRecap,
      notesLabel: t.notesLabel || "Add any notes, questions or concerns here:",
      notesRequired: !!t.notesRequired,
    };
    await set(ref(database, `checklists/templates/${which}`), clean);
    setSavedMsg("Saved!");
    setTimeout(() => setSavedMsg(""), 2500);
  };

  return (
    <div style={card}>
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {["opening", "closing"].map(k => (
          <button key={k} onClick={() => setWhich(k)}
            style={{ ...segBtn(which === k), textTransform: "capitalize" }}>{k}</button>
        ))}
      </div>

      <label style={lbl}>Title</label>
      <input value={t.title} onChange={e => setField("title", e.target.value)} style={inp} />
      <label style={lbl}>Description</label>
      <input value={t.description} onChange={e => setField("description", e.target.value)} style={inp} />

      <div style={{ fontWeight: 700, margin: "12px 0 8px" }}>Checklist items</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {t.items.map((it, idx) => (
          <div key={it.id} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <button onClick={() => moveItem(idx, -1)} title="Move up" style={miniBtn}>▲</button>
              <button onClick={() => moveItem(idx, 1)} title="Move down" style={miniBtn}>▼</button>
            </div>
            <textarea value={it.text} onChange={e => setItemText(it.id, e.target.value)} rows={1}
              style={{ ...inp, marginBottom: 0, resize: "vertical", fontFamily: "inherit" }} />
            <button onClick={() => removeItem(it.id)} title="Remove" style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: 6 }}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <button onClick={addItem} style={{ ...btnGhost, marginTop: 10 }}><Plus size={15} /> Add item</button>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input type="checkbox" checked={!!t.hasAccounting} onChange={e => setField("hasAccounting", e.target.checked)} style={{ width: 18, height: 18 }} />
          Include accounting fields (end of day + month to date)
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input type="checkbox" checked={!!t.hasRecap} onChange={e => setField("hasRecap", e.target.checked)} style={{ width: 18, height: 18 }} />
          Include recap / action plan field
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input type="checkbox" checked={!!t.notesRequired} onChange={e => setField("notesRequired", e.target.checked)} style={{ width: 18, height: 18 }} />
          Require the notes field
        </label>
        <div>
          <label style={lbl}>Notes prompt label</label>
          <input value={t.notesLabel} onChange={e => setField("notesLabel", e.target.value)} style={inp} />
        </div>
      </div>

      <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={save} style={btnPrimary}><Save size={15} /> Save {which} list</button>
        {savedMsg && <span style={{ color: "#16a34a", fontWeight: 600 }}>{savedMsg}</span>}
      </div>
    </div>
  );
}

/* ─────────────────────────── styles ─────────────────────────── */
const card = { background: "#fff", borderRadius: 12, padding: 22, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #f1f5f9" };
const lbl = { fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 };
const inp = { width: "100%", padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: "border-box" };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer", fontWeight: 600, fontSize: 14 };
const btnGhost = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#374151", cursor: "pointer", fontWeight: 600, fontSize: 13 };
const miniBtn = { background: "#f1f5f9", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 9, lineHeight: "12px", padding: "2px 4px", color: "#64748b" };
const segBtn = (active) => ({ padding: "7px 16px", fontSize: 13, fontWeight: 600, border: "none", borderRadius: 8, cursor: "pointer", background: active ? "#7c3aed" : "#f1f5f9", color: active ? "#fff" : "#475569" });
